/**
 * Chat history fetching.
 *
 * Wraps the chat.history protocol method and provides conversion
 * from wire-protocol messages to UI-layer ChatMessage objects.
 */

import type { WSClient } from '../ws/client';
import type {
  ChatHistoryParams,
  ChatHistoryPayload,
  HistoryMessage,
} from '../types/protocol';
import type { ChatMessage } from '../types/chat';
import { normalizeContent } from '../utils/content';
import {
  getAssistantMessageKind,
  shouldHideHistoryAssistantMessage,
} from './assistantMessage';
import { stripQueueDirectiveForDisplay } from './sendWhileRunning';

/**
 * Convert a wire-protocol HistoryMessage to a UI-layer ChatMessage.
 *
 * History messages are marked with `isHistory: true` to allow
 * differentiated rendering (e.g., dimmed styling) in the UI.
 *
 * @param msg - The raw protocol history message
 * @param index - Index in the history array (used for stable ID generation)
 * @param gatewayId - Optional gateway ID to tag the message
 * @param gatewayName - Optional gateway display name
 * @returns A fully typed ChatMessage for UI consumption, or null for hidden protocol-only artifacts
 */
export function historyMessageToChatMessage(
  msg: HistoryMessage,
  index: number,
  gatewayId?: string,
  gatewayName?: string,
): ChatMessage | null {
  // Detect compaction synthetic messages from the gateway
  const openclaw = (msg as unknown as Record<string, unknown>).__openclaw as
    | { kind?: string }
    | undefined;
  if (openclaw?.kind === 'compaction') {
    return {
      id: `history-compaction-${gatewayId ?? 'default'}-${index}-${msg.ts ?? Date.now()}`,
      role: 'system',
      content: '🧹 Context compacted',
      timestamp: msg.ts ?? (msg.timestamp ? new Date(msg.timestamp).toISOString() : new Date().toISOString()),
      isHistory: true,
      isStreaming: false,
      ttsStatus: 'none',
      gatewayId,
      gatewayName,
      isCompaction: true,
    };
  }

  const normalizedContent = normalizeContent(msg.content);
  const content = msg.role === 'user'
    ? stripQueueDirectiveForDisplay(normalizedContent)
    : normalizedContent;
  const assistantKind = msg.role === 'assistant'
    ? getAssistantMessageKind(content)
    : 'message';

  if (msg.role === 'assistant' && shouldHideHistoryAssistantMessage(assistantKind)) {
    return null;
  }

  return {
    id: `history-${gatewayId ?? 'default'}-${index}-${msg.ts ?? Date.now()}`,
    role: msg.role,
    content,
    timestamp: msg.ts ?? (msg.timestamp ? new Date(msg.timestamp).toISOString() : new Date().toISOString()),
    isHistory: true,
    isStreaming: false,
    ttsStatus: 'none',
    gatewayId,
    gatewayName,
    isNoReply: msg.role === 'assistant' && assistantKind === 'no_reply',
    inputTokens: msg.usage?.input ?? msg.usage?.inputTokens,
    outputTokens: msg.usage?.output ?? msg.usage?.outputTokens,
  };
}

/**
 * Fetch raw chat history for a session.
 *
 * Returns the wire-protocol `HistoryMessage[]` directly from the gateway.
 * Use {@link fetchChatHistoryAsMessages} if you need UI-ready `ChatMessage[]`.
 *
 * @param client - The WebSocket client instance
 * @param sessionKey - Session key (e.g., "agent:main:main")
 * @param limit - Maximum number of messages to return (default: 50)
 * @returns Array of history messages (oldest first)
 *
 * @example
 * ```ts
 * const messages = await fetchChatHistory(client, 'agent:main:main', 20);
 * ```
 */
export async function fetchChatHistory(
  client: WSClient,
  sessionKey: string,
  limit: number = 50,
  before?: string,
): Promise<HistoryMessage[]> {
  const params: ChatHistoryParams = {
    sessionKey,
    limit,
    before,
  };

  const payload = await client.request<ChatHistoryParams, ChatHistoryPayload>(
    'chat.history',
    params,
  );

  return payload.messages;
}

/**
 * Fetch chat history and convert to UI-ready ChatMessage objects.
 *
 * Convenience wrapper that calls {@link fetchChatHistory} and maps
 * each result through {@link historyMessageToChatMessage}.
 * All returned messages have `isHistory: true`.
 *
 * @param client - The WebSocket client instance
 * @param sessionKey - Session key (e.g., "agent:main:main")
 * @param limit - Maximum number of messages to return (default: 50)
 * @param gatewayId - Optional gateway ID to tag messages
 * @param gatewayName - Optional gateway display name
 * @returns Array of ChatMessage objects (oldest first)
 */
export async function fetchChatHistoryAsMessages(
  client: WSClient,
  sessionKey: string,
  limit: number = 50,
  gatewayId?: string,
  gatewayName?: string,
  before?: string,
): Promise<ChatMessage[]> {
  const rawMessages = await fetchChatHistory(client, sessionKey, limit, before);
  return rawMessages.flatMap((msg, idx) => {
    const chatMessage = historyMessageToChatMessage(msg, idx, gatewayId, gatewayName);
    return chatMessage ? [chatMessage] : [];
  });
}
